import React from "react";
import { graphql, useRefetchableFragment } from "react-relay";

import { faTimes, faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faDiamond } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce, escapeRegExp, find } from "lodash";
import { Input, type InputOnChangeData } from "semantic-ui-react";

import { KeyboardNavigation } from "~/common";
import { ReactComponent as NoResults } from "~/legacy-assets/images/illustrations/no-results.svg";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { emailFromNameEntry, isValidEmail, isValidName, nameFromEmailEntry, nameFromNameEntry } from "~/lib/utils";
import Avatar from "~/ui/legacy/Avatar";

import type { ContactSearch_contact$data as Contact } from "~/__generated__/ContactSearch_contact.graphql";
import type { ContactSearch_viewer$key as Viewer } from "~/__generated__/ContactSearch_viewer.graphql";
import type { ContactSearchQuery, ContactSearchQuery$data } from "~/__generated__/ContactSearchQuery.graphql";
import type { ContactSearchRefetchQuery } from "~/__generated__/ContactSearchRefetchQuery.graphql";

import styles from "./css/ContactSearch.module.css";

export interface ContactEdge {
	node: Contact;
}

export type SimpleContact = Pick<Contact, "id" | "email" | "fullName" | "user"> & {
	isGroup?: boolean;
	groupSize?: number | null;
};
type Account = ContactSearchQuery$data["viewer"]["account"];
type Domain = Account["domains"][0];

interface ContactSearchResultProps {
	account: Account;
	contact: SimpleContact;
	selected: boolean;
	disabled: boolean;
	regex: RegExp;
	onClick: () => void;
}

function ContactSearchResult({ account, contact, selected, disabled, regex, onClick }: ContactSearchResultProps) {
	const [cursored, setCursored] = React.useState(false);

	const onFocus = React.useCallback(() => {
		setCursored(true);
	}, [setCursored]);

	const onBlur = React.useCallback(() => {
		setCursored(false);
	}, []);

	const { id, fullName, email, user } = contact;
	const { isPersonalType, guestUsersAllowInvite } = account;

	const onEnterDown = React.useCallback(() => {
		const inactiveUser = user ? !user.isActive : false;
		const canSelect = user?.isConfigured || isPersonalType || guestUsersAllowInvite || regex.test(email);

		if (canSelect && !disabled && !inactiveUser) onClick();
	}, [guestUsersAllowInvite, isPersonalType, disabled, email, onClick, regex, user]);

	const canSelect = user?.isConfigured || isPersonalType || guestUsersAllowInvite || regex.test(email);
	const inactiveUser = user && !user.isActive;
	const clickable = canSelect && !disabled && !inactiveUser;
	const wouldBeGuest = guestUsersAllowInvite && !regex.test(email);

	const plusBtnContent = React.useMemo(() => {
		if (user?.internalAttributes?.oneOnOneWithViewer?.archived) return <p className={styles.plusBtnText}>Unarchive</p>;
		else if (inactiveUser) return <p className={styles.plusBtnText}>Deactivated</p>;
		else if (wouldBeGuest) return <p className={styles.plusBtnText}>Guest</p>;
		else if (!user?.isConfigured)
			return (
				<p className={styles.plusBtn}>
					{contact.isGroup ? `${contact.groupSize}+` : <FontAwesomeIcon icon={faUserPlus} />}
				</p>
			);
		return null;
	}, [user, wouldBeGuest, inactiveUser, contact.isGroup, contact.groupSize]);

	return (
		<KeyboardNavigation.Focusable onFocus={onFocus} onBlur={onBlur} onEnterDown={onEnterDown}>
			<div
				key={id}
				onClick={onEnterDown}
				className={cx(styles.result, {
					[styles.selected]: selected,
					[styles.cursored]: cursored,
					[styles.disabled]: !clickable,
				})}
				data-test-id="contact-search-result"
			>
				<div className={styles.info}>
					{contact.isGroup ? (
						<div className={styles.groupAvatar}>
							<FontAwesomeIcon icon={faUsers} />
						</div>
					) : (
						<Avatar user={contact} link={false} />
					)}
					<div className={styles.name}>
						{fullName}
						<div className={styles.email}>{email}</div>
						{!canSelect && (
							<div className={styles.resultError}>
								That doesn&apos;t look like a {account?.name ?? "valid"} email address.
							</div>
						)}
						{inactiveUser && <div className={styles.resultError}>{fullName} is not active on Fellow</div>}
					</div>
				</div>

				{clickable && plusBtnContent}
			</div>
		</KeyboardNavigation.Focusable>
	);
}

interface ContactSearchQueryProps {
	first?: number;
	selection: SimpleContact[];
	excludeConfigured?: boolean;
	excludeExternal?: boolean;
	excludeInactive?: boolean;
	excludeUsers?: boolean;
	filter: FilterFunction;
}

interface ContactSearchResultsProps extends ContactSearchQueryProps {
	viewer: ContactSearchQuery$data["viewer"];
	query: string;
	regex: RegExp;
	fetching: boolean;
	onRefetchComplete: () => void;
	onSelection: (contact: SimpleContact) => void;
}

function ContactSearchResults({
	viewer,
	selection,
	query,
	regex,
	fetching,
	first = 4,
	excludeConfigured = false,
	excludeExternal = false,
	excludeInactive = false,
	excludeUsers = false,
	filter,
	onRefetchComplete,
	onSelection,
}: ContactSearchResultsProps) {
	const [data, refetch] = useRefetchableFragment<ContactSearchRefetchQuery, Viewer>(
		graphql`
			fragment ContactSearch_viewer on Viewer
			@argumentDefinitions(
				search: { type: "String" }
				first: { type: "Int" }
				excludeConfigured: { type: "Boolean" }
				excludeExternal: { type: "Boolean" }
				excludeInactive: { type: "Boolean" }
				excludeUsers: { type: "Boolean" }
			)
			@refetchable(queryName: "ContactSearchRefetchQuery") {
				foundContacts: contacts(
					ranked: true
					search: $search
					first: $first
					excludeConfigured: $excludeConfigured
					excludeExternal: $excludeExternal
					excludeInactive: $excludeInactive
					excludeUsers: $excludeUsers
				) {
					edges {
						node {
							...ContactSearch_contact @relay(mask: false)
						}
					}
				}
			}
		`,
		viewer,
	);

	const { foundContacts } = data;
	const { user, account } = viewer;

	const debouncedRefetch = React.useMemo(() => debounce(refetch, 500), [refetch]);

	React.useEffect(() => {
		debouncedRefetch.cancel();
		if (!query) return onRefetchComplete();

		debouncedRefetch(
			{ search: query, first, excludeConfigured, excludeExternal, excludeInactive, excludeUsers },
			{ onComplete: () => onRefetchComplete() },
		);
	}, [
		query,
		onRefetchComplete,
		debouncedRefetch,
		first,
		excludeConfigured,
		excludeExternal,
		excludeInactive,
		excludeUsers,
	]);

	const toggleContactSelection = React.useCallback(
		(contact: SimpleContact) => {
			onSelection?.(contact);
		},
		[onSelection],
	);

	const onManualNameEntry = React.useCallback(() => {
		if (user) {
			const email = emailFromNameEntry(query, user.domain);
			toggleContactSelection({
				id: email,
				email: emailFromNameEntry(query, user.domain),
				fullName: nameFromNameEntry(query),
				user: null,
			});
		}
	}, [query, user, toggleContactSelection]);

	const onManualEmailEntry = React.useCallback(() => {
		toggleContactSelection({
			id: query,
			email: query,
			fullName: nameFromEmailEntry(query),
			user: null,
		});
	}, [query, toggleContactSelection]);

	function renderContact(contact: SimpleContact, cb: () => void) {
		const selected = find(selection, { id: contact.id });
		return (
			<ContactSearchResult
				key={contact.email}
				account={account}
				contact={contact}
				onClick={cb}
				selected={!!selected}
				disabled={fetching}
				regex={regex}
			/>
		);
	}

	function renderNoResults() {
		return (
			<div className={styles.empty}>
				<NoResults />
				<div>We&apos;ve searched high and low but found no results</div>
			</div>
		);
	}

	if (!foundContacts || !query) return null;

	let contacts = (foundContacts.edges as ContactEdge[]).filter(filter);
	if (!account.allowInviting) {
		contacts = contacts.filter(({ node }) => !!node.user);
	}

	const resultContents = (function () {
		if (contacts.length > 0) {
			return contacts.slice(0, first).map(({ node: contact }) => {
				return renderContact(contact, () => toggleContactSelection(contact));
			});
		}

		if (account.allowInviting && query && foundContacts.edges.length === 0) {
			if (isValidEmail(query)) {
				const contact = {
					id: query,
					email: query,
					fullName: nameFromEmailEntry(query),
					user: null,
				};

				if (query !== user.email) return renderContact(contact, onManualEmailEntry);
			} else if (isValidName(query)) {
				const email = emailFromNameEntry(query, user?.domain ?? "@example.com");
				const contact = {
					id: email,
					email: email,
					fullName: nameFromNameEntry(query),
					user: null,
				};

				if (email !== user.email) return renderContact(contact, onManualNameEntry);
			}
		}

		return renderNoResults();
	})();

	return (
		<div className={styles.results}>
			<KeyboardNavigation>{resultContents}</KeyboardNavigation>
		</div>
	);
}

/**
 * given a ContactEdge, a function that determines whether it should be included in the search – true to include the
 * ContactEdge, false to exclude it
 */
export type FilterFunction = (x: ContactEdge) => boolean;

interface ContactSearchProps extends ContactSearchQueryProps {
	placeholder?: string;
	onEmptyBackspace?: () => void;
	onSelection?: (contact: SimpleContact) => void;
}

graphql`
	fragment ContactSearch_contact on ContactNode {
		id
		fullName
		email
		image
		isGroup
		groupSize
		user {
			id
			email
			firstName
			isActive
			isConfigured
			isGuest
			internalAttributes {
				oneOnOneWithViewer {
					id
					archived
				}
			}
		}
	}
`;

export default function ContactSearch({
	selection,
	placeholder = "",
	onEmptyBackspace,
	onSelection,
	...props
}: ContactSearchProps) {
	const [query, setQuery] = React.useState("");
	const [fetching, setFetching] = React.useState(false);

	const { viewer } = useLazyLoadQuery<ContactSearchQuery>(
		graphql`
			query ContactSearchQuery {
				viewer {
					user {
						id
						domain
						email
					}
					account {
						name
						allowInviting
						guestUsersAllowInvite
						isPersonalType
						emailDomainList
						domains {
							domain
							aliases
						}
					}
					...ContactSearch_viewer
				}
			}
		`,
		{},
		{ fetchPolicy: "store-or-network" },
	);

	const { account } = viewer;

	const domains = React.useMemo(
		() =>
			viewer.account.domains.reduce(
				(acc: string[], domain: Domain) => [...acc, domain.domain, ...domain.aliases],
				[],
			) ?? [],
		[viewer],
	);
	const regex = React.useMemo(() => {
		const pattern = `^[^@]+@(${domains.map(escapeRegExp).join("|")})$`;
		return new RegExp(pattern, "i");
	}, [domains]);

	const onChange = React.useCallback(
		(_event: React.SyntheticEvent<HTMLInputElement, Event>, { value }: InputOnChangeData) => {
			setQuery(value);
			if (value) setFetching(true);
		},
		[setQuery, setFetching],
	);

	const onRefetchComplete = React.useCallback(() => {
		setFetching(false);
	}, [setFetching]);

	const onResultSelection = React.useCallback(
		(contact: SimpleContact) => {
			onSelection?.(contact);
			setQuery("");
		},
		[onSelection],
	);

	const onInputBackspace = React.useCallback(() => {
		if (query) return;
		onEmptyBackspace?.();
	}, [query, onEmptyBackspace]);

	const onInputKeydown = React.useCallback(
		({ keyCode }: React.KeyboardEvent) => {
			if (fetching) return;

			// Remove most recent selection when the user presses backspace
			if (keyCode == 8) onInputBackspace();
		},
		[fetching, onInputBackspace],
	);

	const onLabelClick = React.useCallback(
		(contact: SimpleContact) => {
			return () => onSelection?.(contact);
		},
		[onSelection],
	);

	function buildLabel(contact: SimpleContact) {
		const wouldBeGuest = account.guestUsersAllowInvite && !regex.test(contact.email);
		return (
			<div className={cx("input-label", { unconfigured: !contact.user?.isConfigured })} key={contact.id}>
				{wouldBeGuest && (
					<div className="guest-diamond">
						<FontAwesomeIcon icon={faDiamond} />
					</div>
				)}
				<span className={styles.fullName}>
					{contact.fullName}
					{contact.isGroup && ` (${contact.groupSize} users)`}
				</span>
				<div className="remove-label" onClick={onLabelClick(contact)}>
					<FontAwesomeIcon icon={faTimes} />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.contactSearch} data-test-id="contact-search">
			<Input
				fluid
				transparent
				placeholder={placeholder || `teammate@${domains[0]}`}
				className={styles.input}
				name="contact-search"
				input={<input autoComplete="off" />}
				label={<>{selection.map(buildLabel)}</>}
				value={query}
				loading={fetching}
				onKeyDown={onInputKeydown}
				onChange={onChange}
			/>

			<React.Suspense fallback={<div />}>
				<ContactSearchResults
					viewer={viewer}
					selection={selection}
					query={query}
					regex={regex}
					fetching={fetching}
					onRefetchComplete={onRefetchComplete}
					onSelection={onResultSelection}
					{...props}
				/>
			</React.Suspense>
		</div>
	);
}
